import styles from "./AgentsRoute.module.css";

// Roster of agents. Mirrors AGENT_ROSTER in the legacy
// inrange-agents/frontend/app.jsx file. Pure static data - the page
// is a marketing/architecture view, no API calls.
//
// When a new agent ships, flip its status from "planned" to "built"
// and (optionally) add it to the `owns` list. Atlas already routes
// to whatever specialists exist on the backend so the UI does not
// need to know about new specialists ahead of time.
type AgentStatus = "built" | "planned";

interface Agent {
  id: string;
  name: string;
  role: string;
  status: AgentStatus;
  summary: string;
  owns?: string[];
}

const AGENT_ROSTER: Agent[] = [
  {
    id: "atlas",
    name: "Atlas",
    role: "Managing Director",
    status: "built",
    summary:
      "Maintains a view of the whole delivery portfolio. Triages incoming projects, decides which specialist to invoke, escalates to the CEO when a decision is needed.",
    owns: [
      "Routing every incoming project to the right specialist",
      "Cross-agent prioritisation (which project goes first when EPC capacity is contested)",
      "Composing the CEO-facing escalation pack",
      "The structured-output contract every specialist inherits",
    ],
  },
  {
    id: "engineer",
    name: "Engineer",
    role: "Structural + electrical + PV systems",
    status: "built",
    summary:
      "Reads structural surveys, roof condition reports, design packages. Re-runs loss-factor calculations. Identifies design defects, BOM inconsistencies, simulation errors. Produces the no-cost-commit pre-development pack.",
    owns: [
      "Workstream 3 (on-site survey)",
      "Workstream 4 (structural survey)",
      "Workstream 5 (PV design + SLD + supporting documents)",
      "Pre-construction remediation technical scope",
    ],
  },
  {
    id: "grid",
    name: "Grid",
    role: "DNO / IDNO relationship",
    status: "built",
    summary:
      "Owns the distribution-network counterparty lifecycle: G99 / G100 application, SLA tracking, formal complaint per Ofgem / DCUSA when a DNO breaches, offer analysis, COE / connection-charge dispute handling, host-DNO condition negotiation.",
    owns: [
      "G99 / G100 / EREC applications and the supporting pack",
      "DNO SLA tracking and complaint drafting per published procedures",
      "Connection offer review (contestable / non-contestable split, ICP route)",
      "Policy-cited argument bundles for charge disputes",
    ],
  },
  {
    id: "origination",
    name: "Origination",
    role: "Lead pipeline + qualification",
    status: "planned",
    summary:
      "Watches the lead pipeline. Qualifies new buildings (roof suitability, tenant credit, landlord engagement). Drafts the first commercial conversation. Hands qualified leads to Atlas for assignment.",
  },
  {
    id: "planning",
    name: "Planning",
    role: "Local planning authority + permits",
    status: "planned",
    summary:
      "Prepares permitted-development assessments, planning-portal submissions, response to LPA queries. Tracks SLA on planning decisions.",
  },
  {
    id: "epc",
    name: "EPC Delivery",
    role: "Construction partner management",
    status: "planned",
    summary:
      "Issues EPC tender packs, evaluates bids, tracks site-day-by-site-day construction progress, raises RFIs and snags, signs off on commissioning.",
  },
  {
    id: "procurement",
    name: "Procurement",
    role: "BOM + supplier tendering",
    status: "planned",
    summary:
      "Turns Engineer's design into a procurement-ready bill of materials. Runs supplier tenders. Tracks delivery against the EPC programme.",
  },
  {
    id: "drawdown",
    name: "Drawdown",
    role: "Project-finance evidence packs",
    status: "planned",
    summary:
      "Assembles the evidence pack lenders need at each drawdown gate (legal, technical, commercial). Drafts the drawdown request. Tracks lender conditions.",
  },
  {
    id: "operations",
    name: "Operations",
    role: "Real-time monitoring + commissioning",
    status: "planned",
    summary:
      "Once a site is energised: real-time generation monitoring, anomaly detection, maintenance ticketing, performance reporting against the bankable yield assessment.",
  },
  {
    id: "finance",
    name: "Finance",
    role: "Billing + reconciliation",
    status: "planned",
    summary:
      "Auto-generates reconciled bills (PPA + grid procurement + transfer flows). Manages the cost-approval gate (D18). Surfaces budget variances.",
  },
  {
    id: "comms",
    name: "Comms",
    role: "External correspondence",
    status: "built",
    summary:
      "Drafts every external email / letter the platform produces (DNO complaints, customer updates, lender communications, planning responses, internal-team handovers) in Inrange's voice. Never auto-sends - every draft goes to John (or the named owner) for review and one-click send.",
    owns: [
      "DNO / IDNO correspondence (policy-cited, clause-anchored)",
      "Customer updates (action-by-when-by-whom, plain English)",
      "Lender + equity + board communications (milestone-anchored)",
      "Planning / local-authority responses",
      "Internal-team handover briefs",
    ],
  },
];

const AgentCard = ({ agent }: { agent: Agent }) => {
  const isBuilt = agent.status === "built";
  return (
    <div className={`${styles.card} ${isBuilt ? "" : styles.cardPlanned}`}>
      <div className={styles.head}>
        <div>
          <div className={styles.name}>{agent.name}</div>
          <div className={styles.role}>{agent.role}</div>
        </div>
        <span
          className={`${styles.pill} ${
            isBuilt ? styles.pillBuilt : styles.pillPlanned
          }`}
        >
          {isBuilt ? "Built" : "Planned"}
        </span>
      </div>
      <p className={styles.summary}>{agent.summary}</p>
      {agent.owns && (
        <div className={styles.ownsBlock}>
          <div className={styles.ownsLabel}>Owns</div>
          <ul className={styles.ownsList}>
            {agent.owns.map((item, i) => (
              <li key={i} className={styles.ownsItem}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export const AgentsRoute = () => {
  const builtCount = AGENT_ROSTER.filter((a) => a.status === "built").length;
  const plannedCount = AGENT_ROSTER.length - builtCount;
  return (
    <section className={styles.section}>
      <header className={styles.intro}>
        <h2 className={styles.h2}>Agent roster</h2>
        <p className={styles.lede}>
          The full delivery org as agents. {builtCount} are built and running on
          this platform; {plannedCount} are planned per the design doc. The
          team's job is to commission each one in priority order - Atlas already
          routes work to the {builtCount} that exist.
        </p>
      </header>
      <div className={styles.grid}>
        {AGENT_ROSTER.map((a) => (
          <AgentCard key={a.id} agent={a} />
        ))}
      </div>
    </section>
  );
};
